<script setup lang="ts">
import GuestBookingStepper from "@/components/booking/guestBookingStepper.vue";
import { useGlobalSetupStore } from "@/utils/GlobalSetupStore";
import { pb } from "@/utils/PocketBaseAdapter";
import { useUserStore } from "@/utils/UserStore";
import { moment } from "@/utils/useTimeZone";
import { useUserRole } from "@/utils/useUserRole";
import { ref } from "vue";
import { useRoute, useRouter } from "vue-router";

// stores
const userStore = useUserStore();
const globalStore = useGlobalSetupStore();
const userRole = useUserRole();
const router = useRouter();

const { getCurrentLocation } = userStore;
const { getHomeAlerts } = globalStore;

const { currentLocationDetails, currentLocation, userId, userInfo } =
  storeToRefs(userStore);
const { globalSetup, homeAlerts } = storeToRefs(globalStore);
const { rolesList, permission } = storeToRefs(userRole);

// Data
const currentDateTime = ref(moment().format("YYYY-MM-DD HH:mm:ss"));
const tooltipText = ref("copy");
const activityList = ref<any[]>([]);
const activityUser = ref();
const areaList = ref<any[]>([]);
const initialStep = ref(0);
const showBooking = ref(false);
const activeBooking = ref(false);
const isQuickBooking = ref(false);
const route = useRoute();
const isFromQB = ref(false);

// Computed
const gethomePageImage = computed(() => {
  return `${pb.baseUrl}/api/files/${globalSetup.value.collectionId}/${globalSetup.value.id}/${globalSetup.value.homePageImage}`;
});

const activeAlerts = computed(() => {
  return homeAlerts.value?.filter((alert) => {
    const startTime = moment(alert?.startTime);
    const endTime = moment(alert?.endTime);
    const currentTime = moment(currentDateTime.value);

    return (
      startTime.isSameOrBefore(currentTime) &&
      endTime.isSameOrAfter(currentTime)
    );
  });
});

const getActivities = async () => {
  try {
    const records = await pb.collection("activities").getFullList({
      expand: "availibleTimes,TODPricing,options",
      filter: `location = "${currentLocation.value}"`,
      sort: "sortOrder",
    });
    if (records) {
      activityList.value = records;
    }
  } catch (e) {}
};

// Gets admin id....
const getUsers = async () => {
  const users = await pb.collection("users").getFullList({
    filter: `email = "${currentLocationDetails.value.adminEmail}"`,
  });
  if (users.length > 0) {
    activityUser.value = users[0].id;
  }
};

const getArea = async () => {
  const areas: any = await pb.collection("areas").getFullList();

  areaList.value = areas;
};

const getPayment = async () => {
  const dpid = route?.query?.dpid;
  if (!dpid) return;
  showBooking.value = true;
};

const handleQuickBooking = (e: any) => {
  e.preventDefault();
  showBooking.value = true;
  isQuickBooking.value = true;
};

// Methods
// copy email id to the clipboard
const copyToClipboard = (text: any) => {
  if (!text) return;
  navigator.clipboard
    .writeText(text)
    .then(() => {
      tooltipText.value = "copied";
    })
    .catch((err) => {
      tooltipText.value = "failed to copy";
    })
    .finally(() => {
      setTimeout(() => {
        tooltipText.value = "copy";
      }, 1000);
    });
};
const getMyRole = computed(() => {
  return rolesList?.value?.filter((role: any) =>
    userInfo.value?.roles?.includes(role.id)
  );
});

const subscribeHomeAlerts = async () => {
  try {
    await pb.collection("homeAlerts").subscribe("*", async (res: any) => {
      await getHomeAlerts(currentLocation?.value);
    });
  } catch (e) {
    console.log(e);
  }
};

const handleMemberLogin = () => {
  router.push("auth/login");
  /*
  let BaseURL = window.location.origin;
  if (BaseURL.includes("foreseasons")) {
    const externalLogin = "https://foreseasons.helloclub.com/login"; // put url here
    window.open(externalLogin, "_blank"); // this way open in a new tab.
    // window.location.href = externalLogin; // this way open in the same tab.
  } else {
    router.push("auth/login");
  }

   */
};

const handleGuestBooking = () => {
  showBooking.value = true;
};

const handleGuestWebshop = () => {
  router.push("webshop");
};

// watchers to watch user's current location
watch(currentLocation, async (newCurrentLocation) => {
  await getCurrentLocation();
  await getHomeAlerts(newCurrentLocation);
});

// Function to warn user before leaving the page unload
const warnUser = (event: BeforeUnloadEvent) => {
  let onpayment = localStorage.getItem('onpayment')
  if (showBooking.value && !onpayment) {
    event.preventDefault();
    event.returnValue = ""; // Necessary for Chrome to display the dialog
  }
};

// lifecycle hooks
onMounted(async () => {
  try {
    localStorage.removeItem('onpayment')
  }catch(e){}
  const pendingBookingId = localStorage.getItem("pendingBookingId");
  if (pendingBookingId) {
    try {
      const booking = await pb.collection("bookings").getOne(pendingBookingId);
      if (booking.state === "Pending") {
        await pb.collection("bookings").delete(pendingBookingId);
      }
    } catch (error) {
      console.error("Error handling pending booking:", error);
    } finally {
      localStorage.removeItem("pendingBookingId");
    }
  }
  if (currentLocation.value) {
    await getCurrentLocation();
  }
  window.addEventListener("toggleQuickBooking", ((event: CustomEvent) => {
    if (isFromQB.value) {
      showBooking.value = true;
      isQuickBooking.value = true;
    }
    isFromQB.value = true;
  }) as EventListener);

  window.addEventListener("beforeunload", warnUser);

  userId.value = pb.authStore.model?.id;
  await getHomeAlerts(currentLocation.value);
  await subscribeHomeAlerts();
  await getActivities();
  await getUsers();
  await getArea();
  await getPayment();
  activeBooking.value = true;
  if (route.name == "qb") {
    isFromQB.value = true;
  }
  if (isFromQB.value) {
    showBooking.value = true;
    isQuickBooking.value = true;
  }
});

import { bookingGlobalPopupStore } from "@/components/booking/bookingGlobalPopupStore";
const { openPopup } = bookingGlobalPopupStore()

const closeQuickBooking = (ev) => {
  showBooking.value = ev;
  try {
    let qbid = localStorage.getItem('qbid')
    if (qbid && qbid != '') {
      //openPopup(qbid)
      localStorage.removeItem('qbid')
    }
  }catch(e){}
}

onUnmounted(() => {
  pb.collection("homeAlerts").unsubscribe("*");
  window.removeEventListener("toggleQuickBooking", ((event: CustomEvent) => {
    showBooking.value = event.detail;
  }) as EventListener);
  window.removeEventListener("beforeunload", warnUser);
});
</script>

<template>
  <div>
    <VSheet class="text-center pa-8 mb-3 rounded-sm">
      <div>
        <img
          v-if="gethomePageImage"
          :src="gethomePageImage"
          alt="Baybooker"
          class="center logoImage"
          style="display: block; margin: auto;min-width:220px; height:auto"
        />
      </div>
      <VDivider class="mx-14 my-6" />
      <div class="text-h5">
        Welcome to
        <span class="font-weight-bold">{{ globalSetup.companyName }}</span>
      </div>
      <div v-if="activeAlerts.length" class="mb-2">
        <div
          v-for="alert in activeAlerts"
          :key="alert?.id"
          class="bold_tag my-2"
        >
          <VAlert
            :color="alert?.color || 'error'"
            :type="alert?.type?.toLowerCase()"
            :text="alert?.message"
          />
        </div>
      </div>
    </VSheet>

    <!-- Guest buttons section - only show when user is not logged in -->
    <VCard v-if="!pb?.authStore?.model?.id" class="mb-4">
      <VRow justify="center" class="pa-4">
        <VCol cols="12" sm="6" md="4" lg="3">
          <VBtn
            block
            size="x-large"
            class="me-2 custom-size-btn"
            color="secondary"
            @click="handleMemberLogin"
          >
            Existing Members <br />
            Sign in
          </VBtn>
        </VCol>
        <VCol
          v-if="currentLocationDetails.guestBookingsEnabled"
          cols="12"
          sm="6"
          md="4"
          lg="3"
        >
          <VBtn
            block
            size="x-large"
            :disabled="!activeBooking"
            color="green"
            class="custom-size-btn"
            @click="handleGuestBooking"
          >
            Quick Book <br />
            as a guest
          </VBtn>
        </VCol>
        <VCol
          v-if="currentLocationDetails.webshopEnabled"
          cols="12"
          sm="6"
          md="4"
          lg="3"
        >
          <VBtn
            block
            size="x-large"
            :disabled="!activeBooking"
            color="blue"
            class="custom-size-btn"
            @click="handleGuestWebshop"
          >
            WebShop <br />
            as a guest
          </VBtn>
        </VCol>
      </VRow>
    </VCard>

    <!-- Show 4 Booking of logged in user -->
    <PriorityBookings v-if="pb?.authStore?.model?.id" />

    <VCard class="rounded-sm mt-3" v-if="pb?.authStore?.model?.id">
      <VRow no-gutters>
        <VCol>
          <VSheet class="pa-2 ma-2">
            <VBtn
              :to="`/user/${userStore.userId}`"
              block
              class="me-2"
              prepend-icon="mdi-account-outline"
            >
              Profile
            </VBtn>
          </VSheet>
        </VCol>
        <VCol>
          <VSheet class="pa-2 ma-2">
            <VBtn
              block
              prepend-icon="mdi-calendar-select"
              @click="handleQuickBooking"
            >
              Quick Book
            </VBtn>
          </VSheet>
        </VCol>
        <VCol>
          <VSheet class="pa-2 ma-2">
            <VBtn to="bookings" block prepend-icon="mdi-calendar-select">
              Bookings
            </VBtn>
          </VSheet>
        </VCol>
        <VCol>
          <VSheet class="pa-2 ma-2">
            <VBtn to="calendar" block prepend-icon="mdi-calendar-month-outline">
              Calendar
            </VBtn>
          </VSheet>
        </VCol>
        <!-- <VCol>
          <VSheet class="pa-2 ma-2">
            <VBtn
              to="faqs/user"
              block
              class="me-2"
              prepend-icon="mdi-frequently-asked-questions"
            >
              FAQ
            </VBtn>
          </VSheet>
        </VCol> -->
      </VRow>

      <VRow
        no-gutters
        v-if="permission && permission?.canSeeAdmin?.includes(true)"
      >
        <VCol>
          <VSheet class="pa-2 ma-2">
            <VBtn
              to="/user"
              block
              class="me-2"
              prepend-icon="mdi-account-group-outline"
            >
              Users
            </VBtn>
          </VSheet>
        </VCol>
        <VCol>
          <VSheet class="pa-2 ma-2">
            <VBtn block to="dockets" class="me-2" prepend-icon="mdi-cart">
              Dockets
            </VBtn>
          </VSheet>
        </VCol>
        <VCol>
          <VSheet class="pa-2 ma-2">
            <VBtn to="/pos" block class="me-2" prepend-icon="mdi-network-pos">
              POS
            </VBtn>
          </VSheet>
        </VCol>
        <VCol>
          <VSheet class="pa-2 ma-2">
            <VBtn to="reports" block prepend-icon="mdi-chart-bar">
              Reports
            </VBtn>
          </VSheet>
        </VCol>
      </VRow>
    </VCard>

    <VCard class="mt-6 px-8 py-4 rounded-sm">
      <VRow no-gutters>
        <VCol>
          <VSheet class="text-left">
            <VCardTitle class="ps-4">
              {{ globalSetup.companyName }}
            </VCardTitle>
            <VCardText class="py-0 ps-4">
              <div>
                <p>{{ currentLocationDetails.address }}</p>
                <p>{{ currentLocationDetails.suburb }}</p>
                <p>
                  {{ currentLocationDetails.city }}
                  {{ currentLocationDetails.postcode }}
                </p>
                <p>{{ currentLocationDetails.country }}</p>
                <p class="pt-1">
                  <a
                    :href="`https://maps.google.com/?q=${currentLocationDetails.address},${currentLocationDetails.suburb},${currentLocationDetails.city} ${currentLocationDetails.postcode},${currentLocationDetails.country}`"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View on Google Maps
                    <VIcon icon="mdi-map-marker" size="small" />
                  </a>
                </p>
              </div>
            </VCardText>
          </VSheet>
        </VCol>
        <VCol />

        <VCol>
          <VSheet class="text-left">
            <VCardTitle class="ps-4"> Contact details </VCardTitle>
            <VCardText class="d-flex flex-column align-start ps-4">
              <VBtn v-if="currentLocationDetails?.phone && currentLocationDetails.phone != ''"
                prepend-icon="mdi-phone"
                variant="plain"
                class="px-0"
                @click="copyToClipboard(currentLocationDetails.phone)"
              >
                {{ currentLocationDetails.phone || "phone" }}
                <VTooltip activator="parent" location="right">
                  {{ tooltipText }}
                </VTooltip>
              </VBtn>
              <VBtn
                prepend-icon="mdi-email"
                variant="plain"
                class="px-0"
                @click="copyToClipboard(currentLocationDetails.email)"
              >
                {{ currentLocationDetails.email || "email" }}
                <VTooltip activator="parent" location="right">
                  {{ tooltipText }}
                </VTooltip>
              </VBtn>
              <VBtn
                :href="currentLocationDetails.website"
                prepend-icon="mdi-globe"
                variant="plain"
                class="px-0 website_url"
                target="_blank"
              >
                <div>
                  {{ currentLocationDetails.website || "website" }}
                </div>
              </VBtn>
            </VCardText>
          </VSheet>
        </VCol>
        <VCol />
      </VRow>
    </VCard>
    <GuestBookingStepper
      v-if="showBooking"
      v-model:is-drawer-open="showBooking"
      :location="currentLocation"
      :time-zone="currentLocationDetails.timeZoneCode"
      :activity-list="activityList"
      :area-list="areaList"
      :current-logged-in-roles="getMyRole"
      :user-id="userId ? userId : activityUser"
      :membership="userInfo?.membership"
      :is-quick-booking="isQuickBooking"
      :logged-user="userInfo"
      :initial-step="initialStep"
      @closeDialog="closeQuickBooking($event)"
    />
  </div>
</template>

<style scoped lang="scss">
p {
  margin: 0;
}

.logoImage {
  block-size: 294px;
  inline-size: 400px;
  object-fit: contain;

  @media (max-width: 600px) {
    block-size: auto;
    inline-size: 100%;
  }
}

.custom-size-btn {
  block-size: 124px;
}

@media (max-width: 390px) {
  .website_url {
    margin-inline-start: 10px;
  }
}
</style>
